import { useEffect, useState } from 'react';
import { faThumbsDown } from '@fortawesome/pro-solid-svg-icons/faThumbsDown';
import { faThumbsUp } from '@fortawesome/pro-solid-svg-icons/faThumbsUp';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  BookingService,
  Parent,
  ParentReview,
  ParentReviewService,
  ParentService,
} from 'us-web-services';
import { getShortDateString } from 'us-react-components/dist/utils/date';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';

const ThumbIcon = ({ up }: { up: boolean }): JSX.Element => (
  <FontAwesomeIcon
    icon={up ? faThumbsUp : faThumbsDown}
    className={`mt-1 ${up ? 'text-sapphire' : 'text-neutral-500'}`}
    aria-hidden='true'
  />
);

const RECOMMENDATION_YES = 1;
const RECOMMENDATION_OTHERS = 2;
const RECOMMENDATION_NONE = 3;
const VISIBILITY_PUBLIC = 1;

export const ParentReviews = observer((): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: pathId } = useParams<{ id: string }>();
  const { stores } = useDependencies();
  const user = stores.user.user;

  const parentId = pathId ? Number(pathId) : undefined;

  const [parent, setParent] = useState<Parent | null>(null);
  const [reviews, setReviews] = useState<ParentReview[]>([]);
  const [canReviewParent, setCanReviewParent] = useState(false);
  const [existingReview, setExistingReview] = useState<ParentReview | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'UrbanSitter - Reviews';
  }, []);

  useEffect(() => {
    if (!user?.id || !parentId) return;

    const sitterId = user.sitter ? user.id : undefined;
    let cancelled = false;

    const loadParent = async (): Promise<void> => {
      try {
        const resp = await ParentService.get(parentId, {
          params: {
            sitterId,
            fields: ['badges', 'recommendations'],
            userId: user.id,
          },
        });
        if (cancelled) return;
        setParent(resp.data?.data ?? null);
      } catch (err) {
        console.warn('[ParentReviews] parent load failed', err);
      }
    };

    const loadReviews = async (): Promise<void> => {
      try {
        const resp = await ParentReviewService.getByFilter({
          params: {
            parentId,
            visibilityId: VISIBILITY_PUBLIC,
            userId: user.id,
            sort: 'modified',
            'modified.dir': 'desc',
          },
        });
        if (cancelled) return;
        const data = resp.data?.data ?? [];
        setReviews(data);
        if (sitterId) {
          setExistingReview(data.find((r) => r.sitter?.id === sitterId) ?? null);
        }
      } catch (err) {
        console.warn('[ParentReviews] reviews load failed', err);
      }
    };

    const loadBookings = async (): Promise<void> => {
      try {
        const resp = await BookingService.getByFilter({
          params: {
            limit: 1,
            parentId,
            sitterId,
            userId: user.id,
            statuses: ['closed_completed', 'parent_cancel', 'sitter_cancel'],
          },
        });
        if (cancelled) return;
        const size = resp.data?.meta?.size;
        setCanReviewParent(Boolean(size && size > 0));
      } catch (err) {
        console.warn('[ParentReviews] bookings load failed', err);
      }
    };

    void (async (): Promise<void> => {
      await Promise.allSettled([loadParent(), loadReviews(), loadBookings()]);
      if (!cancelled) setLoading(false);
    })();

    return (): void => {
      cancelled = true;
    };
  }, [user?.id, user?.sitter, parentId]);

  const formatModifiedDate = (review: ParentReview): string =>
    getShortDateString(review.modified);

  const goReview = (): void => {
    if (!parent?.id) return;
    navigate(`/parent/${parent.id}/feedback`);
  };

  if (loading) {
    return (
      <div className='container py-8 text-center'>
        <p className='text-neutral-600'>{t('common.loading')}</p>
      </div>
    );
  }

  if (!parent?.id) {
    return (
      <div className='container py-8 text-center'>
        <p className='text-neutral-600'>{t('pages.parentReviews.unavailable')}</p>
      </div>
    );
  }

  const getTitle = (review: ParentReview): string => {
    const parentName = review.parent?.firstName ?? '';
    const sitterName = review.sitter?.firstName ?? '';
    switch (review.recommendation?.id) {
      case RECOMMENDATION_YES:
        return t('pages.parentReviews.card.titleYes', { name: parentName });
      case RECOMMENDATION_OTHERS:
        return t('pages.parentReviews.card.titleOthers', {
          sitter: sitterName,
          parent: parentName,
        });
      case RECOMMENDATION_NONE:
        return t('pages.parentReviews.card.titleNone', { name: parentName });
      default:
        return '';
    }
  };

  const getAccuracyLine = (review: ParentReview): string =>
    review.accurateJobDescription
      ? t('pages.parentReviews.card.accurateYes')
      : t('pages.parentReviews.card.accurateNo');

  const getPaysLine = (review: ParentReview): string =>
    review.paysBasedOnBooking
      ? t('pages.parentReviews.card.paysYes')
      : t('pages.parentReviews.card.paysNo');

  const getRespectsLine = (review: ParentReview): string =>
    review.respectsYourTime
      ? t('pages.parentReviews.card.respectsYes')
      : t('pages.parentReviews.card.respectsNo');

  const parentFirstName = parent.firstName ?? '';
  const rec = parent.recommendation;

  return (
    <div className='container py-8'>
      {rec?.reviewCount && rec.reviewCount > 0 ? (
        <div className='bg-sapphire-50 rounded p-4 text-center mb-6 max-w-2xl mx-auto'>
          <h3 className='text-lg font-semibold mb-2'>{t('pages.parentReviews.fyi')}</h3>
          <div className='mb-2'>
            {t('pages.parentReviews.fyiRecommend', {
              rating: rec.rating,
              name: parentFirstName,
            })}
          </div>
          <div className='italic text-sm'>
            {t('pages.parentReviews.fyiBasedOn', {
              count: rec.reviewCount,
            })}
          </div>
        </div>
      ) : null}

      <h1 className='text-2xl font-semibold mb-2'>
        {t('pages.parentReviews.heading', { name: parentFirstName })}
      </h1>

      <div className='mb-4 text-sm'>
        <Link to={`/parent/${parent.id}`} className='underline'>
          {t('pages.parentReviews.backLink', { name: parentFirstName })}
        </Link>{' '}
        |{' '}
        <a
          href='https://support.urbansitter.com/hc/en-us/articles/4404385784219'
          target='_blank'
          rel='noopener noreferrer'
          className='underline'
        >
          {t('pages.parentReviews.learnMore')}
        </a>
      </div>

      {canReviewParent && (
        <div className='mb-6'>
          <Button
            buttonType={existingReview ? ButtonType.secondary : ButtonType.primary}
            onClick={goReview}
          >
            {existingReview
              ? t('pages.parentReviews.editReview')
              : t('pages.parentReviews.reviewParent')}
          </Button>
        </div>
      )}

      <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
        {reviews.length === 0 ? (
          <div>
            <img
              src='https://assets.urbansitter.com/static/img/backgrounds/background-check-town.svg'
              alt='Background-checked sitters'
              className='w-full mb-4'
            />
            <p className='mb-4'>
              {t('pages.parentReviews.emptyNoFeedback', { name: parentFirstName })}
            </p>
            <p>
              {t('pages.parentReviews.emptyOnceCaregivers', { name: parentFirstName })}
            </p>
          </div>
        ) : (
          reviews.map((review) => (
            <div key={review.id} className='us-card rounded-[20px]'>
              <div className='flex items-start gap-3 mb-3'>
                <Link to={`/sitter/${review.sitter?.id}`} id={`sitter-${review.sitter?.id}`}>
                  {review.sitter?.pictureUrl && (
                    <img
                      src={review.sitter.pictureUrl}
                      alt={review.sitter.firstName ?? ''}
                      className='w-16 h-16 rounded-full object-cover'
                    />
                  )}
                </Link>
                <div>
                  <p className='font-medium mb-1'>{review.sitter?.firstName}</p>
                  <p className='text-sm text-neutral-600'>
                    {t('pages.parentReviews.card.reviewedOn', {
                      date: formatModifiedDate(review),
                    })}
                  </p>
                </div>
              </div>
              <p className='font-semibold mb-3'>{getTitle(review)}</p>
              <ul className='space-y-2 text-sm list-none pl-0'>
                <li className='flex items-start gap-2'>
                  <ThumbIcon up={!!review.respectsYourTime} />
                  <span>{getRespectsLine(review)}</span>
                </li>
                <li className='flex items-start gap-2'>
                  <ThumbIcon up={!!review.accurateJobDescription} />
                  <span>{getAccuracyLine(review)}</span>
                </li>
                <li className='flex items-start gap-2'>
                  <ThumbIcon up={!!review.paysBasedOnBooking} />
                  <span>{getPaysLine(review)}</span>
                </li>
                {review.recommendation?.id === RECOMMENDATION_NONE && (
                  <li className='flex items-start gap-2'>
                    <ThumbIcon up={false} />
                    <span>
                      {t('pages.parentReviews.card.recommendsToOthers', {
                        name: review.parent?.firstName ?? '',
                      })}
                    </span>
                  </li>
                )}
              </ul>
            </div>
          ))
        )}

        <div className='us-card rounded-[20px]'>
          <p className='mb-4 text-sm'>{t('pages.parentReviews.communityDisclaimer')}</p>
          <div className='text-center'>
            <a
              id='community-guidelines'
              href='https://support.urbansitter.com/hc/en-us/articles/115000630368-Community-Guidelines'
              target='_blank'
              rel='noopener noreferrer'
              className='font-semibold underline'
            >
              {t('pages.parentReviews.communityGuidelines')}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
});
